import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export interface Stream {
  stream_url: string
  stream_key: string
}

export interface StreamContextProps {
  stream: Stream | null
  updateStream: (data: Stream) => Promise<boolean | void>
  startStream: () => Promise<boolean | void>
  stopStream: () => Promise<boolean | void>
  loading: boolean
}

export const StreamContext = React.createContext<
  StreamContextProps | undefined
>(undefined)

export default function StreamProvider(props: {children: React.ReactElement}) {
  const apiClient = organizationApiClient()
  const [stream, setStream] = useState<Stream | null>(null)
  const [initLoading, setInitLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)
  const eventId = useRouteOrganizationEventId()

  useEffect(() => {
    setInitLoading(true)
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    setLoading(true)
    await apiClient
      .get(`api/event/${eventId}/meeting/stream-url`)
      .then((res) => setStream(res))
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => {
        setLoading(false)
        setInitLoading(false)
      })
  }

  const updateStream = async (data: Stream) => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/meeting/stream-url`, data)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  const startStream = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/meeting/start-stream`)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  const stopStream = async () => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/meeting/stop-stream`)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return true

    return false
  }

  return (
    <StreamContext.Provider
      value={{
        stream,
        updateStream,
        startStream,
        stopStream,
        loading,
      }}
    >
      {initLoading && <FullPageLoader />}
      {!initLoading && props.children}
    </StreamContext.Provider>
  )
}

export function useStream() {
  const context = React.useContext(StreamContext)
  if (context === undefined) {
    throw new Error('useStream must be used within StreamProvider')
  }
  return context
}
